import { CircleCheck, CircleX, RotateCcw, Trophy, X } from 'lucide-react';
import { Accordion, Badge, Button, Card, Eyebrow, ProgressBar, cx } from '../../components/charte';
import { lettersLabel, seriesTitle, type QuestionView, type RoadCodeSeries, type SeriesStart } from '../../lib/roadCode';
import { FullScreen } from './SeriesPlayer';
import { QuestionCard } from './QuestionCard';
import { SourcesLine } from './SourcesLine';

/** Message d'encouragement selon le score. */
function verdict(series: RoadCodeSeries) {
  const result = series.result!;
  if (series.mode === 'exam')
    return result.passed
      ? { title: 'Examen blanc réussi !', text: 'Continuez sur cette lancée : visez 5 examens blancs réussis d’affilée.' }
      : {
          title: 'Pas encore…',
          text: `Il faut ${result.passMark} bonnes réponses sur ${result.total} pour réussir. Revoyez vos erreurs, puis retentez.`,
        };
  const rate = result.total ? result.correct / result.total : 0;
  if (rate >= 0.9) return { title: 'Excellent !', text: 'Vous maîtrisez ces questions.' };
  if (rate >= 0.7) return { title: 'Bien joué !', text: 'Encore un petit effort sur vos erreurs.' };
  return { title: 'Continuez !', text: 'Lisez les corrections : c’est en revoyant ses erreurs qu’on progresse le plus.' };
}

/**
 * Résultat d'une série terminée : score, verdict (examen blanc réussi à 35/40), détail par thème,
 * puis la correction de chaque question (erreurs d'abord dépliables). Relancer ses erreurs ou une
 * nouvelle série se fait d'ici.
 */
export function SeriesResultView({
  series,
  themes,
  onClose,
  onStart,
  starting,
}: {
  series: RoadCodeSeries;
  themes: Array<{ key: string; label: string }>;
  onClose: () => void;
  onStart: (start: SeriesStart) => void;
  starting: boolean;
}) {
  const result = series.result!;
  const { title, text } = verdict(series);
  const questions = series.questions.filter((q): q is QuestionView => !('missing' in q && q.missing));
  const mistakes = questions.filter((q) => q.answer && !q.answer.correct);
  const again: SeriesStart = { mode: series.mode, ...(series.theme ? { theme: series.theme } : {}), feedback: series.feedback };
  const success = series.mode === 'exam' ? !!result.passed : result.correct / Math.max(result.total, 1) >= 0.7;
  return (
    <FullScreen label="Résultat de la série" onEscape={onClose}>
      <header className="shrink-0 border-b border-line bg-white safe-top">
        <div className="mx-auto flex h-14 max-w-2xl items-center gap-3 px-3">
          <Button variant="ghost" iconOnly icon={X} aria-label="Retour à l’accueil du code" onClick={onClose} />
          <h1 className="min-w-0 flex-1 truncate text-center text-[15px] font-semibold text-heading">{seriesTitle(series.mode, series.theme, themes)}</h1>
          <span className="w-10" aria-hidden />
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-2xl gap-4 px-4 py-5">
          <section
            className={cx(
              'rounded-card p-6 text-center text-white shadow-card',
              success ? 'bg-success-dark' : 'bg-panel-indigo',
            )}
            aria-label="Votre score"
          >
            <Trophy size={28} className="mx-auto opacity-90" aria-hidden />
            <p className="mt-2 text-[44px] font-semibold leading-none tracking-[-2px] tabular-nums">
              {result.correct}
              <span className="text-[22px] font-medium opacity-80">/{result.total}</span>
            </p>
            <p className="mt-2 text-lg font-semibold">{title}</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-white/85">{text}</p>
          </section>

          <div className="grid gap-2 sm:grid-cols-2">
            {mistakes.length > 0 && (
              <Button variant="primary" icon={RotateCcw} className="h-12" loading={starting} onClick={() => onStart({ mode: 'mistakes' })}>
                Revoir mes erreurs
              </Button>
            )}
            <Button className="h-12" disabled={starting} onClick={() => onStart(again)}>
              Nouvelle série
            </Button>
          </div>

          {result.byTheme.length > 1 && (
            <Card>
              <Eyebrow as="h2">Par thème</Eyebrow>
              <ul className="mt-3 grid gap-3">
                {result.byTheme.map((theme) => (
                  <li key={theme.theme}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-[13.5px]">
                      <span className="text-body">{theme.label}</span>
                      <span className="shrink-0 font-semibold tabular-nums text-heading">
                        {theme.correct}/{theme.total}
                      </span>
                    </div>
                    <ProgressBar
                      value={(theme.correct / theme.total) * 100}
                      tone={theme.correct === theme.total ? 'success' : 'primary'}
                      label={`${theme.label} : ${theme.correct} sur ${theme.total}`}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <section aria-label="Correction">
            <h2 className="mb-2 text-title-sm text-heading">Correction</h2>
            <ul className="grid gap-2">
              {questions.map((question) => {
                const answer = question.answer;
                const good = !!answer?.correct;
                const prompt = question.parts.map((part) => part.prompt).join(' ');
                return (
                  <li key={question.position}>
                    <Accordion
                      defaultOpen={false}
                      leading={
                        good ? (
                          <CircleCheck size={20} className="shrink-0 text-success" aria-label="Bonne réponse" />
                        ) : (
                          <CircleX size={20} className="shrink-0 text-danger" aria-label="Mauvaise réponse" />
                        )
                      }
                      title={
                        <span className="line-clamp-2">
                          <span className="text-secondary">{question.position + 1}. </span>
                          {question.intro ? `${question.intro} ${prompt}` : prompt}
                        </span>
                      }
                      meta={!good && <Badge tone="danger">{answer?.selected.length ? lettersLabel(answer.selected) : 'Sans réponse'}</Badge>}
                    >
                      <div className="-mx-4 -mt-3">
                        <QuestionCard question={question} selected={answer?.selected ?? []} expected={answer?.expected} locked />
                      </div>
                      <p className="mt-3 font-medium text-heading">Réponse attendue : {lettersLabel(answer?.expected ?? [])}</p>
                      {answer?.explanation && <p className="mt-1 text-body">{answer.explanation}</p>}
                      <SourcesLine sources={answer?.sources} className="mt-1.5" />
                    </Accordion>
                  </li>
                );
              })}
            </ul>
          </section>
          <Button variant="ghost" onClick={onClose} className="justify-self-center">
            Retour à l’accueil du code
          </Button>
        </div>
      </main>
    </FullScreen>
  );
}
